import { useRef, useEffect, useState } from 'preact/hooks'

interface ButtonProps {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  className?: string
  type?: 'primary' | 'secondary'
  color?: 'primary' | 'danger'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  title?: string | undefined
}

export function Button({
  children,
  onClick,
  disabled,
  className = '',
  type = 'primary',
  color = 'primary',
  size = 'md',
  fullWidth = false,
  title,
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isPressed, setIsPressed] = useState(false)
  const [isTouchDevice] = useState(() => 'ontouchstart' in window)

  useEffect(() => {
    const button = buttonRef.current
    if (!button || !isTouchDevice || disabled || type === 'secondary') return

    const handleTouchStart = () => {
      setIsPressed(true)
    }

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault()
      setIsPressed(false)
      if (onClick) {
        onClick()
      }
    }

    const handleTouchCancel = () => {
      setIsPressed(false)
    }

    button.addEventListener('touchstart', handleTouchStart, { passive: false })
    button.addEventListener('touchend', handleTouchEnd, { passive: false })
    button.addEventListener('touchcancel', handleTouchCancel, {
      passive: false,
    })

    return () => {
      button.removeEventListener('touchstart', handleTouchStart)
      button.removeEventListener('touchend', handleTouchEnd)
      button.removeEventListener('touchcancel', handleTouchCancel)
    }
  }, [onClick, disabled, isTouchDevice, type])

  const sizeClasses = {
    xs: 'h-8 px-4 text-button-xs',
    sm: 'h-[40px] px-6 text-button-sm',
    md: 'h-[48px] px-8 text-button-sm',
    lg: 'h-[60px] px-10 text-button',
  }

  const widthClass = fullWidth ? 'w-full' : 'w-auto'
  const pressedClasses =
    isPressed && type === 'primary' ? 'scale-[0.98]' : 'scale-100'

  // Primary button styles (gradient with layers like CTAButton)
  if (type === 'primary') {
    const gradientClasses = {
      primary: {
        base: 'bg-btn-gradient',
        hover: 'bg-btn-gradient-hover',
        pressed: 'bg-btn-gradient-pressed',
      },
      danger: {
        base: 'bg-btn-gradient-danger',
        hover: 'bg-btn-gradient-danger-hover',
        pressed: 'bg-btn-gradient-danger-pressed',
      },
    }

    const currentGradient = gradientClasses[color]

    return (
      <button
        ref={buttonRef}
        onClick={isTouchDevice ? undefined : onClick}
        disabled={disabled}
        style={{
          WebkitTapHighlightColor: 'transparent',
          ...(isPressed && {
            filter: 'brightness(1.05)',
          }),
        }}
        title={title}
        className={`group relative ${widthClass} ${sizeClasses[size]} rounded-2xl text-white font-semibold uppercase border-none cursor-pointer isolate overflow-hidden select-none touch-manipulation focus:outline-none focus:shadow-button-focus hover:shadow-button-hover hover:scale-[1.01] active:scale-95 transition-all duration-150 ease-out disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 disabled:hover:scale-100 ${pressedClasses} ${className}`}
      >
        {/* Background gradient layer */}
        <div
          className={`absolute inset-0 rounded-2xl transition-all duration-150 ease-out z-[-2] ${
            isPressed ? currentGradient.pressed : currentGradient.base
          }`}
        />

        {/* Hover gradient layer */}
        {!isPressed && (
          <div
            className={`absolute inset-0 rounded-2xl transition-all duration-150 ease-out z-[-1] ${currentGradient.hover} opacity-0 group-hover:opacity-100`}
          />
        )}

        {/* Active gradient layer */}
        {!isPressed && (
          <div
            className={`absolute inset-0 rounded-2xl transition-all duration-150 ease-out z-[-1] ${currentGradient.pressed} opacity-0 group-active:opacity-100`}
          />
        )}

        {/* Gloss layer */}
        <div
          className={`absolute inset-0 rounded-2xl transition-all duration-150 ease-out z-[0] ${
            isPressed ? 'bg-btn-gloss-pressed' : 'bg-btn-gloss'
          }`}
        />

        {/* Content */}
        <span className='relative z-10'>{children}</span>
      </button>
    )
  }

  // Secondary button styles (flat design)
  const secondaryClasses = {
    primary: {
      base: 'bg-white text-cencert-header-blue border border-gray-200',
      hover: 'bg-gray-50 border-gray-300',
      active: 'bg-gray-100 border-gray-400',
    },
    danger: {
      base: 'bg-white text-red-700 border border-red-200',
      hover: 'bg-red-50 border-red-300',
      active: 'bg-red-100 border-red-400',
    },
  }

  const currentSecondary = secondaryClasses[color]

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      className={`
        ${widthClass} ${sizeClasses[size]} 
        rounded-2xl font-semibold uppercase
        cursor-pointer select-none touch-manipulation 
        focus:outline-none focus:ring-2 focus:ring-offset-2
        ${color === 'primary' ? 'focus:ring-blue-300' : 'focus:ring-red-300'}
        hover:scale-[1.01] active:scale-95 
        transition-all duration-150 ease-out 
        disabled:opacity-50 disabled:cursor-not-allowed 
        disabled:active:scale-100 disabled:hover:scale-100
        ${currentSecondary.base}
        hover:${currentSecondary.hover}
        active:${currentSecondary.active}
        ${className}
      `}
    >
      {children}
    </button>
  )
}
